import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, GripVertical, Calendar, Clock } from "lucide-react";
import { cn } from "@/lib/utils";

export interface VisitCadenceItem {
  id: string;
  name: string;
  type: string;
  days_from_enrollment: number;
  window_before: number;
  window_after: number;
  billable: boolean;
  visit_modality?: string;
}

interface VisitCadenceEditorProps {
  visits: VisitCadenceItem[];
  onChange: (visits: VisitCadenceItem[]) => void;
  disabled?: boolean;
}

// Must match database enum: screening, baseline, week_4, week_8, month_6, end_of_study, unscheduled, adverse_event
const VISIT_TYPES = [
  { value: 'screening', label: 'Screening' },
  { value: 'baseline', label: 'Baseline' },
  { value: 'week_4', label: 'Week 4' },
  { value: 'week_8', label: 'Week 8' },
  { value: 'month_6', label: 'Month 6' },
  { value: 'end_of_study', label: 'End of Study' },
  { value: 'unscheduled', label: 'Unscheduled' },
  { value: 'adverse_event', label: 'Adverse Event' },
];

const VISIT_MODALITIES = [
  { value: 'in_person', label: 'In-Person' },
  { value: 'virtual', label: 'Virtual' },
  { value: 'home_nursing', label: 'Home Nursing' },
  { value: 'hybrid', label: 'Hybrid' },
];

export function VisitCadenceEditor({ visits, onChange, disabled }: VisitCadenceEditorProps) {
  const [editingId, setEditingId] = useState<string | null>(null);

  const addVisit = () => {
    const lastVisit = visits[visits.length - 1];
    const newVisit: VisitCadenceItem = {
      id: crypto.randomUUID(),
      name: `Visit ${visits.length + 1}`,
      type: 'follow_up',
      days_from_enrollment: (lastVisit?.days_from_enrollment || 0) + 28,
      window_before: 3,
      window_after: 3,
      billable: true,
      visit_modality: 'in_person',
    };
    onChange([...visits, newVisit]);
    setEditingId(newVisit.id);
  };

  const updateVisit = (id: string, updates: Partial<VisitCadenceItem>) => {
    onChange(visits.map(v => v.id === id ? { ...v, ...updates } : v));
  };

  const removeVisit = (id: string) => {
    onChange(visits.filter(v => v.id !== id));
  };

  const formatDays = (days: number) => {
    if (days === 0) return 'Day 0 (Enrollment)';
    if (days < 0) return `${Math.abs(days)} days before enrollment`;
    if (days < 7) return `Day ${days}`;
    if (days < 30) return `Week ${Math.round(days / 7)}`;
    return `Month ${Math.round(days / 30)}`;
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Visit Schedule
            </CardTitle>
            <CardDescription>
              Define the visit cadence for this protocol
            </CardDescription>
          </div>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={addVisit}
            disabled={disabled}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Visit
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {visits.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            No visits defined. Click "Add Visit" to create a visit schedule.
          </div>
        ) : (
          <div className="space-y-3">
            {visits.map((visit, index) => (
              <div
                key={visit.id}
                className={cn(
                  "border rounded-lg p-3 transition-colors",
                  editingId === visit.id ? "ring-2 ring-primary" : "hover:bg-accent/50"
                )}
              >
                <div className="flex items-start gap-3">
                  <div className="flex items-center gap-2 text-muted-foreground pt-1">
                    <GripVertical className="h-4 w-4" />
                    <Badge variant="outline" className="text-xs">
                      #{index + 1}
                    </Badge>
                  </div>

                  {editingId === visit.id ? (
                    // Edit mode
                    <div className="flex-1 grid gap-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Visit Name</Label>
                          <Input
                            value={visit.name}
                            onChange={(e) => updateVisit(visit.id, { name: e.target.value })}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Visit Type</Label>
                          <Select
                            value={visit.type}
                            onValueChange={(value) => updateVisit(visit.id, { type: value })}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {VISIT_TYPES.map(type => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Visit Modality</Label>
                          <Select
                            value={visit.visit_modality || 'in_person'}
                            onValueChange={(value) => updateVisit(visit.id, { visit_modality: value })}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {VISIT_MODALITIES.map(mod => (
                                <SelectItem key={mod.value} value={mod.value}>
                                  {mod.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Days from Enrollment</Label>
                          <Input
                            type="number"
                            value={visit.days_from_enrollment}
                            onChange={(e) => updateVisit(visit.id, { days_from_enrollment: parseInt(e.target.value) || 0 })}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Window Before (days)</Label>
                          <Input
                            type="number"
                            min="0"
                            value={visit.window_before}
                            onChange={(e) => updateVisit(visit.id, { window_before: parseInt(e.target.value) || 0 })}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Window After (days)</Label>
                          <Input
                            type="number"
                            min="0"
                            value={visit.window_after}
                            onChange={(e) => updateVisit(visit.id, { window_after: parseInt(e.target.value) || 0 })}
                            className="h-8 text-sm"
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={visit.billable}
                            onCheckedChange={(checked) => updateVisit(visit.id, { billable: checked })}
                          />
                          <Label className="text-xs text-muted-foreground">Billable visit</Label>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingId(null)}
                        >
                          Done
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // View mode
                    <div 
                      className="flex-1 cursor-pointer"
                      onClick={() => !disabled && setEditingId(visit.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{visit.name}</span>
                            <Badge variant="secondary" className="text-xs">
                              {VISIT_TYPES.find(t => t.value === visit.type)?.label || visit.type}
                            </Badge>
                            {visit.billable && (
                              <Badge variant="outline" className="text-xs text-green-600">
                                Billable
                              </Badge>
                            )}
                            {visit.visit_modality && visit.visit_modality !== 'in_person' && (
                              <Badge variant="outline" className="text-xs">
                                {VISIT_MODALITIES.find(m => m.value === visit.visit_modality)?.label}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDays(visit.days_from_enrollment)}
                            </span>
                            {(visit.window_before > 0 || visit.window_after > 0) && (
                              <span>
                                Window: -{visit.window_before}/+{visit.window_after} days
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeVisit(visit.id);
                          }}
                          disabled={disabled}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {visits.length > 0 && (
          <div className="mt-4 p-3 bg-muted rounded-lg">
            <p className="text-xs text-muted-foreground">
              <strong>Total Duration:</strong> {visits.length > 0 ? 
                `${Math.max(...visits.map(v => v.days_from_enrollment))} days from enrollment` : 
                'N/A'
              } • <strong>Billable Visits:</strong> {visits.filter(v => v.billable).length} of {visits.length}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
